import {
    Badge,
    BadgeType,
    CollectionPaymentSourceType,
    DataTablePresenter,
    ITableColumn,
    ProviderCollectionPayment,
    renderAmount,
    renderDate,
    TimelineSegment,
} from '@enter/core';
import _ from 'lodash';
import { CollectionRefundDialog } from 'pages/main/claims/components/collection-refund-dialog';
import React, { useContext, useEffect, useMemo } from 'react';
import { DialogContext } from 'services';
import { ActionButton } from './action-button';
import * as S from './details.styles';

interface IProps {
    payments: ProviderCollectionPayment[] | undefined;
}

export const Payments = ({ payments }: IProps) => {
    const dialogContext = useContext(DialogContext);

    const handleRefund = (payment: ProviderCollectionPayment) => {
        const handleSubmit = () => {
            dialogContext.hideDialog();
        };

        const handleCloseDialog = () => {
            dialogContext.hideDialog();
        };

        dialogContext.showDialog({
            props: {
                onClose: handleCloseDialog,
                disableBackdropClick: true,
                disableEscapeKeyDown: true,
                renderTitle: 'Refund Payment',
            },
            Component: (
                <CollectionRefundDialog onClose={handleCloseDialog} paymentId={payment.id!} onSubmit={handleSubmit} />
            ),
        });
    };

    const { current: presenter } = React.useRef(
        new DataTablePresenter<ProviderCollectionPayment>({
            name: 'provider-guarantor-payments',
            data: payments || [],
            columns: [],
            filterSchemas: [],
        }),
    );

    const paymentsColumns: ITableColumn<ProviderCollectionPayment>[] = useMemo(
        () => [
            {
                id: 'segment',
                Cell: ({ cell }): React.ReactElement => (
                    <S.StyledSegment>
                        <TimelineSegment
                            start={cell.row.index === 0}
                            end={cell.row.index === _.size(presenter.data) - 1}
                        />
                    </S.StyledSegment>
                ),
                minWidth: 50,
                maxWidth: 50,
            },
            {
                id: 'Source',
                Header: 'Source',
                accessor: 'source',
                maxWidth: 150,
                Cell: ({ cell }) => {
                    const isFrontOffice =
                        cell.value === CollectionPaymentSourceType.PREPAYMENT ||
                        cell.value === CollectionPaymentSourceType.OFFICEPAYMENT;

                    return (
                        <S.Cell>
                            <Badge type={isFrontOffice ? BadgeType.GREEN : BadgeType.BLUE_LIGHT}>
                                {isFrontOffice ? 'Front Office' : 'Enter'}
                            </Badge>
                        </S.Cell>
                    );
                },
                disableSortBy: true,
            },
            {
                id: 'CollectedAmount',
                Header: 'Total Collected',
                accessor: 'collectedAmount',
                Cell: ({ cell }) => <S.Cell>{renderAmount(cell.value)}</S.Cell>,
                disableSortBy: true,
            },
            {
                id: 'PaymentDate',
                Header: 'Payment Date',
                accessor: 'paymentDate',
                Cell: ({ cell }) => <S.Cell>{renderDate(cell.value)}</S.Cell>,
                disableSortBy: true,
            },
        ],
        [presenter.data],
    );

    useEffect(() => {
        presenter.onColumnsUpdate([
            ...paymentsColumns,
            {
                id: 'Action',
                Header: '',
                disableSortBy: true,
                width: 0,
                Cell: ({ row }): React.ReactElement =>
                    row.original.source === CollectionPaymentSourceType.PREPAYMENT ? (
                        <></>
                    ) : (
                        <ActionButton
                            actions={[
                                {
                                    label: 'Refund Payment',
                                    onClick: () => handleRefund(row.original),
                                },
                            ]}
                        />
                    ),
            },
        ]);
    }, [presenter, paymentsColumns]);

    useEffect(() => {
        presenter.onDataUpdate(payments || []);
        presenter.onDataTotalChange(_.size(payments));
    }, [presenter, payments]);

    return (
        <S.StyledTitleCard size="large" info="Shows associated guarantor payments" title="Collection Payments">
            <S.StyledDataTable
                presenter={presenter}
                isLoading={!payments}
                disableSorting={true}
                hideFiltering={true}
                hideNavigation={true}
            />
        </S.StyledTitleCard>
    );
};
